import { useState } from 'react'
import { SkillOrigin } from '@/analytics/enums/skill.enums'
import { Skill, XClose } from '@/assets/icons'
import { Button } from '@/components/button/Button'
import { IconButton } from '@/components/icon-button/IconButton'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import type { UserSkillsLibrary } from '@/user-skills/hooks/useUserSkills'
import type { PaxtonSkill, UserSkill } from '@/user-skills/schemas'
import { UserSkillSelectionActions } from '@/user-skills/store/selection.slice'
import DeleteUserSkillDialog from './DeleteUserSkillDialog'
import SkillViewer from './SkillViewer'
import UserSkillEditor from './UserSkillEditor'

type SelectedSkillsProps = { conversationId: string; disabled: boolean; library: UserSkillsLibrary }

export default function SelectedSkills(props: SelectedSkillsProps) {
  const selection = useAppSelector((state) => state.userSkillSelectionState.byConversationId[props.conversationId])
  if (!selection || (!selection.selectedSkillIds.length && !selection.selectedPaxtonSkillNames.length)) return null

  return <SelectedSkillsContent {...props} skillIds={selection.selectedSkillIds} paxtonSkillNames={selection.selectedPaxtonSkillNames} />
}

function SelectedSkillsContent({
  conversationId,
  disabled,
  skillIds,
  paxtonSkillNames,
  library,
}: SelectedSkillsProps & { skillIds: string[]; paxtonSkillNames: string[] }) {
  const dispatch = useAppDispatch()
  const { skills, paxtonSkills, loading, loadError, refresh, saveSkill, removeSkill } = library
  const [viewingUserSkill, setViewingUserSkill] = useState<UserSkill | null>(null)
  const [viewingPaxtonSkill, setViewingPaxtonSkill] = useState<PaxtonSkill | null>(null)
  const [editingSkill, setEditingSkill] = useState<UserSkill | null>(null)
  const [deletingSkill, setDeletingSkill] = useState<UserSkill | null>(null)
  const rows = [
    ...skillIds.map((id) => {
      const skill = skills.find((item) => item.id === id)
      return {
        key: id,
        name: skill?.name,
        available: !!skill,
        onOpen: () => skill && setViewingUserSkill(skill),
        onRemove: () =>
          dispatch(UserSkillSelectionActions.selectSkills({ conversationId, skillIds: skillIds.filter((item) => item !== id), paxtonSkillNames })),
      }
    }),
    ...paxtonSkillNames.map((id) => {
      const skill = paxtonSkills.find((item) => item.id === id)
      return {
        key: id,
        name: skill?.name,
        available: !!skill,
        onOpen: () => skill && setViewingPaxtonSkill(skill),
        onRemove: () =>
          dispatch(UserSkillSelectionActions.selectSkills({ conversationId, skillIds, paxtonSkillNames: paxtonSkillNames.filter((item) => item !== id) })),
      }
    }),
  ]

  return (
    <>
      <div
        className="mb-2 flex max-h-[60px] flex-wrap gap-x-3 gap-y-1 overflow-y-auto overscroll-contain sm:max-h-none sm:overflow-visible"
        role="group"
        aria-label="Attached skills"
      >
        {rows.map((row) => (
          <div key={row.key} className="flex min-w-0 max-w-full items-center">
            <Button
              type="button"
              variant="plain"
              size="sm"
              leadingIcon={<Skill className="size-4" aria-hidden />}
              aria-label={`View ${row.name ?? 'selected skill'}`}
              disabled={loading || loadError || !row.available}
              onClick={row.onOpen}
              className="min-w-0 !h-auto"
            >
              <span className="min-w-0 truncate text-left sm:whitespace-normal sm:break-words">
                {row.name ?? (loading ? 'Loading skill…' : 'Unavailable skill')}
              </span>
            </Button>
            <IconButton
              type="button"
              variant="plain"
              color="neutral"
              size="sm"
              wrapperClassName="shrink-0"
              aria-label={`Remove ${row.name ?? 'selected skill'}`}
              icon={<XClose />}
              disabled={disabled}
              onClick={row.onRemove}
            />
          </div>
        ))}
        {loadError && (
          <div role="alert" className="w-full text-sm text-brand-neutral-600">
            Could not load attached skills.{' '}
            <Button type="button" variant="plain" size="sm" onClick={() => void refresh()}>
              Retry
            </Button>
          </div>
        )}
      </div>
      {viewingUserSkill && (
        <SkillViewer
          skill={viewingUserSkill}
          origin={SkillOrigin.User}
          onClose={() => setViewingUserSkill(null)}
          onEdit={
            disabled
              ? undefined
              : () => {
                  setViewingUserSkill(null)
                  setEditingSkill(viewingUserSkill)
                }
          }
          onDelete={
            disabled
              ? undefined
              : () => {
                  setViewingUserSkill(null)
                  setDeletingSkill(viewingUserSkill)
                }
          }
        />
      )}
      {viewingPaxtonSkill && <SkillViewer skill={viewingPaxtonSkill} origin={SkillOrigin.Paxton} onClose={() => setViewingPaxtonSkill(null)} />}
      {editingSkill && (
        <UserSkillEditor
          skill={editingSkill}
          skillId={editingSkill.id}
          onClose={() => setEditingSkill(null)}
          onSave={(content) => saveSkill(editingSkill.id, content)}
        />
      )}
      {deletingSkill && <DeleteUserSkillDialog skill={deletingSkill} onClose={() => setDeletingSkill(null)} onDelete={() => removeSkill(deletingSkill.id)} />}
    </>
  )
}
